// The CEO overview blocks on Today (docs/specs/overview.md). Each block reads
// only what the viewer may see; a block with nothing to show stays hidden.

import { Link } from 'react-router-dom';
import { stageLabel } from '@ouradent/shared';
import {
  mondayOf,
  useAdSpend,
  useCalendar,
  useClients,
  useOverview,
  useTeam,
  type OverviewMetrics,
} from '../../lib/data';
import { fTime } from '../../lib/format';
import type { BoardLead, Task } from '../../lib/types';
import { Icon } from '../../ui/icons';
import { Chead, useDrawer } from '../../ui/kit';
import { useIntegrations } from '../Integrations';

function Delta({ now, prev, label }: { now: number; prev: number; label: string }) {
  const d = now - prev;
  const cls = d > 0 ? 'up' : d < 0 ? 'down' : '';
  return (
    <div className={`s delta ${cls}`}>
      {d > 0 ? '+' : ''}
      {d} vs {label} ({prev})
    </div>
  );
}

export function Kpis() {
  const q = useOverview();
  const m = q.data;
  if (!m) return <div className="kpis k5 loading-kpis" aria-hidden />;
  const sources = Object.entries(m.sources_week).sort((a, b) => b[1] - a[1]);
  const showRate = m.booked_week
    ? Math.round(((m.booked_week - m.noshow_week) / m.booked_week) * 100)
    : null;
  return (
    <div className="kpis k5" aria-label="This week in numbers">
      <div className="card kpi">
        <div className="k">New leads this week</div>
        <div className="v">{m.leads_week}</div>
        <Delta now={m.leads_week} prev={m.leads_prev} label="last week" />
        {sources.length > 0 && (
          <div className="s">{sources.map(([s, n]) => `${s} ${n}`).join(', ')}</div>
        )}
      </div>
      <div className="card kpi">
        <div className="k">Discovery calls booked</div>
        <div className="v">{m.booked_week}</div>
        <Delta now={m.booked_week} prev={m.booked_prev} label="last week" />
        {showRate !== null && <div className="s">Show rate {showRate}%</div>}
      </div>
      <div className="card kpi">
        <div className="k">Calls held</div>
        <div className="v">{m.calls_week}</div>
        <Delta now={m.calls_week} prev={m.calls_prev} label="last week" />
      </div>
      <div className="card kpi">
        <div className="k">Offers sent</div>
        <div className="v">{m.offers_week}</div>
        <Delta now={m.offers_week} prev={m.offers_prev} label="last week" />
      </div>
      <div className="card kpi">
        <div className="k">Clients won this month</div>
        <div className="v">{m.won_month}</div>
        <Delta now={m.won_month} prev={m.won_prev} label="last month" />
      </div>
    </div>
  );
}

export function Funnel({ m }: { m: OverviewMetrics | undefined }) {
  const { open } = useDrawer();
  if (!m) return null;
  const f = m.funnel;
  const steps: [string, number][] = [
    ['Leads', f.leads],
    ['Call', f.call],
    ['Offer', f.offer],
    ['Won', f.won],
  ];
  return (
    <section className="card" aria-label="Funnel">
      <Chead label="Funnel, last 30 days" cls="p">
        <Link className="viewall" to="/pipeline">
          Pipeline <Icon name="arrow" />
        </Link>
      </Chead>
      <div className="funnel">
        {steps.map(([label, n], i) => (
          <div key={label} className="fstep">
            <div className="fbar">
              <i style={{ width: `${f.leads ? Math.max(4, (n / f.leads) * 100) : 0}%` }} />
            </div>
            <span className="fl">{label}</span>
            <b>{n}</b>
            <span className="fr">
              {i > 0 && steps[i - 1]![1] ? `${Math.round((n / steps[i - 1]![1]) * 100)}%` : ''}
            </span>
          </div>
        ))}
      </div>
      {m.stuck.length > 0 && (
        <>
          <div className="subh">Stuck for 14 days or more</div>
          <div className="list-pad">
            {m.stuck.map((s) => (
              <button key={s.id} className="orow" onClick={() => open({ type: 'lead', id: s.id })}>
                <span className="t">{s.clinic}</span>
                <span className="m">{stageLabel(s.stage)}</span>
                <span className="pill sm or">{s.days} days</span>
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export function Meetings() {
  const { open } = useDrawer();
  const d = new Date();
  const from = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
  const to = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).toISOString();
  const q = useCalendar(from, to);
  const events = (q.data ?? []).filter((e) => e.status !== 'cancelled');
  return (
    <section className="card" aria-label="Meetings today">
      <Chead label="Meetings today" cls="g">
        <Link className="viewall" to="/calendar">
          Calendar <Icon name="arrow" />
        </Link>
      </Chead>
      <div className="list-pad">
        {events.length ? (
          events.map((e) => (
            <button key={e.id} className="orow" onClick={() => open({ type: 'event', id: e.id })}>
              <span className="tm">{e.all_day ? 'All day' : fTime(e.start_at)}</span>
              <span className="t">{e.summary ?? '(no title)'}</span>
              {e.lead_id && <span className="pill sm">Lead</span>}
            </button>
          ))
        ) : (
          <div className="empty">No meetings today.</div>
        )}
      </div>
    </section>
  );
}

export function ClientsCard({ tasks }: { tasks: Task[] }) {
  const { open } = useDrawer();
  const clients = useClients();
  const now = new Date();
  const rows = (clients.data ?? [])
    .map((c) => {
      const list = tasks.filter((t) => t.client_id === c.id && !t.done_at);
      const late = list.filter((t) => new Date(t.due_at) < now).length;
      const next = [...list].sort((a, b) => a.due_at.localeCompare(b.due_at))[0];
      return { c, open: list.length, late, next };
    })
    .sort((a, b) => b.late - a.late || b.open - a.open);
  if (!rows.length) return null;
  return (
    <section className="card" aria-label="Clients">
      <Chead label="Clients" cls="g">
        <Link className="viewall" to="/tasks">
          Tasks <Icon name="arrow" />
        </Link>
      </Chead>
      <div className="list-pad">
        {rows.map(({ c, open: n, late, next }) => (
          <button
            key={c.id}
            className="orow"
            onClick={() =>
              next ? open({ type: 'task', id: next.id }) : open({ type: 'task', clientId: c.id })
            }
          >
            <span className="cdot" style={{ background: c.colour }} aria-hidden />
            <span className="t">
              {c.name}
              <small>{next ? `Next: ${next.text}` : 'Nothing open'}</small>
            </span>
            <span className="pill sm gr">{n} open</span>
            {late > 0 && <span className="pill sm red">{late} overdue</span>}
          </button>
        ))}
      </div>
    </section>
  );
}

export function TeamCard({ tasks, leads }: { tasks: Task[]; leads: BoardLead[] }) {
  const team = useTeam();
  const now = new Date();
  const people = (team.data ?? []).filter((p) => !p.external);
  if (people.length < 2) return null;
  return (
    <section className="card" aria-label="Team workload">
      <Chead label="Team workload" cls="p" />
      <div className="list-pad">
        {people.map((p) => {
          const mine = tasks.filter((t) => t.owner_id === p.id && !t.done_at);
          const late = mine.filter((t) => new Date(t.due_at) < now).length;
          const active = leads.filter(
            (l) => l.owner_id === p.id && !['won', 'lost', 'disq', 'nurture'].includes(l.stage),
          ).length;
          return (
            <div key={p.id} className="orow">
              <span className="t">{p.full_name}</span>
              <span className="m">{active} active leads</span>
              <span className="pill sm gr">{mine.length} tasks</span>
              {late > 0 && <span className="pill sm red">{late} overdue</span>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function AdsCard({ leads }: { leads: BoardLead[] }) {
  const spend = useAdSpend();
  const since = new Date(`${mondayOf()}T00:00:00`);
  const by = new Map<string, number>();
  for (const l of leads) {
    if (new Date(l.created_at) < since) continue;
    const k = l.campaign ?? l.source ?? 'Unknown';
    by.set(k, (by.get(k) ?? 0) + 1);
  }
  const paid = spend.data?.campaigns ?? [];
  const eur = (n: number) => `€${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  // With Meta spend connected: spend, CRM leads and cost per lead per
  // campaign; without it: the lead count per campaign or source.
  const rows = paid.length
    ? paid.slice(0, 6).map((c) => ({
        k: c.name,
        n: c.leads,
        meta: `${eur(c.spend)} spent${c.cpl !== null ? `, ${eur(c.cpl)} per lead` : ', no lead yet'}`,
      }))
    : [...by.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([k, n]) => ({ k, n, meta: null as string | null }));
  return (
    <section className="card" aria-label="Leads by campaign">
      <Chead label="Leads by campaign this week" cls="g">
        {paid.length > 0 && (
          <span className="viewall" style={{ cursor: 'default' }}>
            {eur(spend.data!.spend)} spent
          </span>
        )}
      </Chead>
      <div className="list-pad">
        {rows.length ? (
          rows.map((r) => (
            <div key={r.k} className="orow">
              <span className="t">
                {r.k}
                {r.meta && <small>{r.meta}</small>}
              </span>
              <b>{r.n}</b>
            </div>
          ))
        ) : (
          <div className="empty">No new leads yet this week.</div>
        )}
        {!paid.length && (
          <p className="hint">Cost per lead appears once Meta ad spend is connected.</p>
        )}
      </div>
    </section>
  );
}

export function Warnings() {
  const q = useIntegrations();
  // Only integrations that worked before and stopped; one never connected is
  // not a fault, it is on the Integrations screen.
  const bad = (q.data ?? []).filter((x) => x.status === 'error' && x.last_event_at);
  if (!bad.length) return null;
  // One line: the detail lives on the Integrations screen.
  return (
    <section className="card warn" aria-label="System warnings">
      <Link className="orow" to="/integrations">
        <span className="pill sm red">Error</span>
        <span className="t">
          {bad.length === 1
            ? `${bad[0]!.label} stopped working`
            : `${bad.length} integrations stopped working`}
          <small>{bad.map((x) => x.label).join(', ')}</small>
        </span>
        <Icon name="chev" />
      </Link>
    </section>
  );
}
